import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { RecordsService, UserStoreService } from '@mediahaven/shared';
import {
    ApiFragment,
    ApiQuery,
    BatchActions,
    BatchObject,
    BatchSearchRequest,
    BatchService,
    BulkSearchRequest,
    Combine,
    DottedKey,
    Family,
    MediaObjectId,
    MediaType,
    PartialRecord,
    RecordId,
    RecordRestrictions,
    RecordStatus,
    RecordType,
    Restriction,
    User,
    ZoneId,
    ZoneRestrictions,
    defaultSortInfo,
    singleResultPaginationInfo
} from '@stylehaven/components';
import { isEmpty } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class IntellectualRecordsService {
    constructor(
        private readonly recordsService: RecordsService,
        private readonly userStoreService: UserStoreService,
        private readonly zoneRestrictions: ZoneRestrictions,
        private readonly batchService: BatchService
    ) {}

    public get mediaHavenUser$(): Observable<User> {
        return this.userStoreService.getCurrentUser();
    }

    // #region Generic Intellectual RecordTypes

    public getNrOfExportableChildren(parentId: RecordId): Observable<number> {
        return this.mediaHavenUser$.pipe(
            take(1),
            switchMap(user => {
                const restriction: Restriction = Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.exportableRestriction(user.Groups),
                    RecordRestrictions.excludeConceptPhaseRestriction(),
                    RecordRestrictions.childOfParentRestriction(parentId)
                );
                return this.recordsService.getRecords(
                    singleResultPaginationInfo,
                    defaultSortInfo,
                    new ApiQuery([], restriction)
                );
            }),
            map(paginatedResult => paginatedResult.paginationInfo.totalNumberOfResults)
        );
    }

    public getNrOfNonExportableChildren(parentId: RecordId): Observable<number> {
        return this.mediaHavenUser$.pipe(
            take(1),
            switchMap(user => {
                const restriction: Restriction = Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.nonExportableRestriction(user.Groups),
                    RecordRestrictions.childOfParentRestriction(parentId)
                );
                return this.recordsService.getRecords(
                    singleResultPaginationInfo,
                    defaultSortInfo,
                    new ApiQuery([], restriction)
                );
            }),
            map(paginatedResult => paginatedResult.paginationInfo.totalNumberOfResults)
        );
    }

    public getNrOfEmbeddableChildren(parentId: RecordId): Observable<number> {
        return this.mediaHavenUser$.pipe(
            take(1),
            map(user => user.Groups),
            switchMap(groups => {
                const publishedRestriction: Restriction = Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.publishedPhaseRestriction(),
                    RecordRestrictions.childOfParentRestriction(parentId),
                    RecordRestrictions.publicRestriction(groups)
                );

                return this.recordsService.getRecords(
                    singleResultPaginationInfo,
                    defaultSortInfo,
                    new ApiQuery([], publishedRestriction)
                );
            }),
            map(paginatedResult => paginatedResult.paginationInfo.totalNumberOfResults)
        );
    }

    public getAllExportableChildren(parentId: RecordId): Observable<ApiFragment[]> {
        return this.mediaHavenUser$.pipe(
            take(1),
            switchMap(user => {
                const restriction: Restriction = Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.exportableRestriction(user.Groups),
                    RecordRestrictions.excludeConceptPhaseRestriction(),
                    RecordRestrictions.childOfParentRestriction(parentId)
                );

                return this.recordsService.getAllRecords(new ApiQuery([], restriction), {
                    annotate: false
                });
            })
        );
    }

    public getAllPublishedChildren(
        parentId: RecordId,
        fieldSelect: DottedKey[] = ['Internal.FragmentId'] as DottedKey[]
    ): Observable<ApiFragment[]> {
        const publishedPhaseRestriction: Restriction = Combine.And(
            this.zoneRestrictions.regularRecordTypesZone(),
            RecordRestrictions.publishedPhaseRestriction(),
            RecordRestrictions.childOfParentRestriction(parentId)
        );
        return this.recordsService.getAllRecords(new ApiQuery([], publishedPhaseRestriction, fieldSelect), {
            annotate: false
        });
    }

    public getInvalidChildIds(parentId: RecordId): Observable<RecordId[]> {
        return this.recordsService.getChildIds(parentId, [
            RecordStatus.DraftInvalid,
            RecordStatus.Processing,
            RecordStatus.Rejected,
            RecordStatus.New
        ]);
    }
    // #endregion

    // #region Collections

    public createIntellectualRecord(
        partialFragment: PartialRecord,
        zoneId: ZoneId,
        recordType: RecordType,
        parentId?: MediaObjectId
    ): Observable<ApiFragment> {
        partialFragment.set(Family.Administrative, 'RecordType', recordType);
        partialFragment.set(Family.Administrative, 'Type', MediaType.metadataonly);

        return this.mediaHavenUser$.pipe(
            switchMap(user => {
                const hasUploader: boolean = partialFragment.get(Family.Descriptive, 'UploadedBy') !== null;

                // MED-1990: Manually add UploadedBy if not set through form
                if (!hasUploader) {
                    const userFullName: string = [user.FirstName, user.LastName].join(' ').trim();
                    const uploadedBy: string = isEmpty(userFullName) ? user.Login : userFullName;
                    partialFragment.set(Family.Descriptive, 'UploadedBy', uploadedBy);
                }

                return this.recordsService.createRecord(partialFragment, {
                    parentRecordId: parentId,
                    zoneId
                });
            })
        );
    }

    public bulkCoupleRecordsToParent(
        fragments: ApiFragment[],
        parent: ApiFragment,
        batchTag?: string
    ): Observable<BatchObject> {
        const searchRequest: BulkSearchRequest = new BulkSearchRequest(
            fragments,
            this.zoneRestrictions.allNonDeletedRecordsZone()
        );
        return this.batchService.sendBatch(BatchActions.COUPLE_TO_PARENT, {
            fragments,
            searchRequest,
            parent,
            waitForFinish: true,
            isBulk: true,
            batchTag
        });
    }

    public batchCoupleRecordsToParent(
        query: ApiQuery,
        parent: ApiFragment,
        batchTag?: string
    ): Observable<BatchObject> {
        const searchRequest: BatchSearchRequest = new BatchSearchRequest(query);
        return this.batchService.sendBatch(BatchActions.COUPLE_TO_PARENT, {
            fragments: [],
            parent,
            searchRequest,
            isBulk: false,
            batchTag
        });
    }

    public setAsCover(record: ApiFragment, collection: ApiFragment): Observable<ApiFragment> {
        return this.recordsService.setAsCover(record, collection);
    }

    // #endregion
}
